import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ProjectNote {
  id: string;
  project_id: string;
  title: string;
  body: string;
  note_type: 'note' | 'update' | 'milestone' | 'issue' | 'meeting_notes';
  created_by: string | null;
  created_at: string;
  updated_at: string;
  profiles?: {
    display_name: string | null;
    email: string;
  } | null;
}

export const useProjectNotes = (projectId?: string) => {
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchNotes = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('project_notes')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes((data || []) as ProjectNote[]);
    } catch (error) {
      console.error('Error fetching project notes:', error);
      toast({
        title: 'Fout bij laden notities',
        description: 'Er ging iets mis bij het ophalen van de notities.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const createNote = async (noteData: {
    title: string;
    body: string;
    note_type: 'note' | 'update' | 'milestone' | 'issue' | 'meeting_notes';
  }) => {
    if (!projectId) return;

    try {
      const { data, error } = await supabase
        .from('project_notes')
        .insert({
          project_id: projectId,
          ...noteData
        })
        .select('*')
        .single();

      if (error) throw error;

      setNotes(prev => [data as ProjectNote, ...prev]);
      toast({
        title: 'Notitie toegevoegd',
        description: 'De notitie is succesvol toegevoegd.',
      });

      return data;
    } catch (error) {
      console.error('Error creating note:', error);
      toast({
        title: 'Fout bij aanmaken notitie',
        description: 'Er ging iets mis bij het aanmaken van de notitie.',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const updateNote = async (noteId: string, updates: {
    title?: string;
    body?: string;
    note_type?: 'note' | 'update' | 'milestone' | 'issue' | 'meeting_notes';
  }) => {
    try {
      const { data, error } = await supabase
        .from('project_notes')
        .update(updates)
        .eq('id', noteId)
        .select('*')
        .single();

      if (error) throw error;

      setNotes(prev => prev.map(note => 
        note.id === noteId ? { ...note, ...(data as ProjectNote) } : note
      ));

      toast({
        title: 'Notitie bijgewerkt',
        description: 'De notitie is succesvol bijgewerkt.',
      });
    } catch (error) {
      console.error('Error updating note:', error);
      toast({
        title: 'Fout bij bijwerken notitie',
        description: 'Er ging iets mis bij het bijwerken van de notitie.',
        variant: 'destructive'
      });
    }
  };

  const deleteNote = async (noteId: string) => {
    try {
      const { error } = await supabase
        .from('project_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;

      setNotes(prev => prev.filter(note => note.id !== noteId));
      toast({
        title: 'Notitie verwijderd',
        description: 'De notitie is succesvol verwijderd.',
      });
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: 'Fout bij verwijderen notitie',
        description: 'Er ging iets mis bij het verwijderen van de notitie.',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [projectId]);

  return {
    notes,
    loading,
    createNote,
    updateNote,
    deleteNote,
    refetch: fetchNotes
  };
};